/**
 * The persistent left rail inside the Hubs (HubSpot-style; Jon, 2026-11-03):
 * All Hubs, Home, Pinned, then the shared and department Hubs, then Admin, each
 * with its icon. Rows for Hubs with items open a fly-out (hover with a short
 * delay, or click/tap/Enter); other rows are plain links. Collapses to icons only
 * (labels become tooltips). Hidden below 768 px, where <HubDrawer> takes over.
 *
 * Keyboard: ↑/↓/Home/End move along the rail; → / Enter / Space open a fly-out
 * and focus its first entry; inside a fly-out ↑/↓ move, Esc or ← close it and
 * return focus to its row.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faAnglesLeft, faAnglesRight, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { DWA } from '../theme/dwaTokens';
import { HubMenu, PinnedMenu } from './HubMenu';
import { moveFocus, rowStyle, useRailEntries } from './railModel';
import type { RailEntry } from './railModel';
import { Tooltip } from './Tooltip';

export const RAIL_WIDTH = 232;
export const RAIL_WIDTH_COLLAPSED = 64;
const FLYOUT_WIDTH = 260;
const OPEN_DELAY = 140;
const CLOSE_DELAY = 260;

export const RowIcon: React.FC<{ icon: IconDefinition; active: boolean }> = ({ icon, active }) => (
  <span style={{ width: 20, display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }} aria-hidden="true">
    <FontAwesomeIcon icon={icon} style={{ fontSize: '0.95rem', color: active ? DWA.gold : 'rgba(248,250,252,0.8)' }} />
  </span>
);

const Flyout: React.FC<{
  entry: Extract<RailEntry, { kind: 'menu' }>;
  anchor: HTMLElement | null;
  rail: HTMLElement | null;
  panelRef: React.RefObject<HTMLDivElement>;
  onClose: (returnFocus: boolean) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}> = ({ entry, anchor, rail, panelRef, onClose, onMouseEnter, onMouseLeave }) => {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!anchor || !rail) return;
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const r = rail.getBoundingClientRect();
      const h = panelRef.current?.offsetHeight ?? 0;
      const top = Math.max(8, Math.min(a.top - 8, window.innerHeight - h - 8));
      setPos({ top, left: r.right + 6 });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor, rail, panelRef, entry.key]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'ArrowLeft') {
      e.preventDefault();
      e.stopPropagation();
      onClose(true);
      return;
    }
    if (moveFocus(panelRef.current, '[data-menu-item]', e.key)) e.preventDefault();
  };

  return (
    <div
      ref={panelRef}
      id={`rail-flyout-${entry.key}`}
      role="group"
      aria-label={`${entry.label} menu`}
      data-testid={`rail-flyout-${entry.key}`}
      onKeyDown={onKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'fixed',
        top: pos?.top ?? 64,
        left: pos?.left ?? RAIL_WIDTH + 6,
        width: FLYOUT_WIDTH,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        overflowY: 'auto',
        background: DWA.railFlyout,
        border: `1px solid ${DWA.railLine}`,
        borderRadius: DWA.radius,
        boxShadow: '0 18px 40px -12px rgba(0,0,0,0.65)',
        padding: '0.5rem',
        zIndex: 160,
        fontFamily: DWA.font,
      }}
    >
      <p style={{ margin: 0, padding: '0.35rem 0.75rem 0.45rem', color: DWA.onDark, fontWeight: 700, fontSize: '0.95rem' }}>{entry.label}</p>
      {entry.hub ? (
        <HubMenu hub={entry.hub} variant="flyout" onNavigate={() => onClose(false)} />
      ) : (
        <PinnedMenu variant="flyout" onNavigate={() => onClose(false)} />
      )}
    </div>
  );
};

export const HubRail: React.FC<{
  collapsed: boolean;
  /** Omitted when the collapse state is fixed (the Lupa Hub keeps the rail narrow). */
  onToggleCollapsed?: () => void;
  sticky?: boolean;
}> = ({ collapsed, onToggleCollapsed, sticky = true }) => {
  const entries = useRailEntries();
  const { pathname, search } = useLocation();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const openedBy = useRef<'hover' | 'click'>('click');
  const railRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Record<string, HTMLElement | null>>({});
  const timer = useRef<number | undefined>(undefined);
  const focusFirst = useRef(false);

  const openKeyRef = useRef<string | null>(null);
  openKeyRef.current = openKey;

  const cancel = () => window.clearTimeout(timer.current);
  const close = useCallback((returnFocus = false) => {
    window.clearTimeout(timer.current);
    const key = openKeyRef.current;
    setOpenKey(null);
    if (returnFocus && key) rowRefs.current[key]?.focus();
  }, []);

  // Close on navigation.
  useEffect(() => {
    close(false);
  }, [pathname, search, close]);

  // Close on a click or focus outside the rail and the fly-out.
  useEffect(() => {
    if (!openKey) return;
    const outside = (target: EventTarget | null) =>
      target instanceof Node && !railRef.current?.contains(target) && !panelRef.current?.contains(target);
    const onDown = (e: MouseEvent) => outside(e.target) && close(false);
    const onFocus = (e: FocusEvent) => outside(e.target) && close(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close(true);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('focusin', onFocus);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('keydown', onKey);
    };
  }, [openKey, close]);

  // After a keyboard open, move focus into the fly-out.
  useEffect(() => {
    if (!openKey || !focusFirst.current) return;
    focusFirst.current = false;
    panelRef.current?.querySelector<HTMLElement>('[data-menu-item]')?.focus();
  }, [openKey]);

  useEffect(() => cancel, []);

  const hoverOpen = (key: string) => {
    cancel();
    timer.current = window.setTimeout(() => {
      openedBy.current = 'hover';
      setOpenKey(key);
    }, OPEN_DELAY);
  };
  const hoverClose = () => {
    cancel();
    if (openedBy.current === 'click') return;
    timer.current = window.setTimeout(() => setOpenKey(null), CLOSE_DELAY);
  };

  const clickRow = (key: string) => {
    cancel();
    if (openKey === key && openedBy.current === 'hover') {
      openedBy.current = 'click'; // a click on a hover-opened fly-out keeps it open
      return;
    }
    openedBy.current = 'click';
    setOpenKey((k) => (k === key ? null : key));
  };

  const onRailKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (!target.matches('[data-rail-row]')) return;
    const key = target.dataset.railKey;
    const entry = entries.find((x) => x.key === key);
    if (entry?.kind === 'menu' && (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      cancel();
      openedBy.current = 'click';
      focusFirst.current = true;
      if (openKey === entry.key) panelRef.current?.querySelector<HTMLElement>('[data-menu-item]')?.focus();
      else setOpenKey(entry.key);
      return;
    }
    if (moveFocus(railRef.current, '[data-rail-row]', e.key)) {
      e.preventDefault();
      close(false);
    }
  };

  const width = collapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH;
  const openEntry = entries.find((x): x is Extract<RailEntry, { kind: 'menu' }> => x.kind === 'menu' && x.key === openKey);

  return (
    <nav
      ref={railRef}
      id="hub-rail"
      aria-label="Hubs"
      className="ow-rail"
      data-testid="hub-rail"
      data-collapsed={collapsed ? 'true' : 'false'}
      style={{
        width,
        flexShrink: 0,
        background: DWA.rail,
        borderRight: `1px solid ${DWA.railLine}`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.15s ease',
        ...(sticky ? { position: 'sticky', top: 56, height: 'calc(100vh - 56px)', alignSelf: 'flex-start' } : {}),
        zIndex: 140,
      }}
    >
      <ul
        onKeyDown={onRailKeyDown}
        style={{ listStyle: 'none', margin: 0, padding: '0.6rem 0.5rem', flex: 1, overflowY: 'auto', overflowX: 'hidden' }}
      >
        {entries.map((entry) => {
          if (entry.kind === 'divider') {
            return <li key={entry.key} role="separator" aria-hidden="true" style={{ height: 1, background: DWA.railLine, margin: '0.5rem 0.4rem' }} />;
          }
          const body = (
            <>
              <RowIcon icon={entry.icon} active={entry.active} />
              {!collapsed && <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.label}</span>}
              {!collapsed && entry.kind === 'menu' && (
                <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" style={{ fontSize: '0.65rem', color: 'rgba(248,250,252,0.5)' }} />
              )}
            </>
          );
          const common = {
            ref: (el: HTMLElement | null) => {
              rowRefs.current[entry.key] = el;
            },
            'data-rail-row': true,
            'data-rail-key': entry.key,
            'data-testid': `rail-row-${entry.key}`,
            className: 'ow-rail-row',
            style: rowStyle(entry.active, collapsed),
            'aria-label': collapsed ? entry.label : undefined,
          };
          const row =
            entry.kind === 'link' ? (
              <Link
                {...common}
                to={entry.to}
                aria-current={entry.active ? 'page' : undefined}
                onMouseEnter={() => openKey && hoverClose()}
              >
                {body}
              </Link>
            ) : (
              <button
                {...common}
                type="button"
                aria-expanded={openKey === entry.key}
                aria-controls={openKey === entry.key ? `rail-flyout-${entry.key}` : undefined}
                aria-haspopup="true"
                aria-current={entry.active ? 'true' : undefined}
                onClick={() => clickRow(entry.key)}
                onMouseEnter={() => hoverOpen(entry.key)}
                onMouseLeave={hoverClose}
              >
                {body}
              </button>
            );
          return (
            <li key={entry.key} style={{ margin: '0.1rem 0' }}>
              <Tooltip content={entry.label} disabled={!collapsed || openKey === entry.key} delay={150}>
                {row}
              </Tooltip>
            </li>
          );
        })}
      </ul>
      {onToggleCollapsed && (
        <div style={{ borderTop: `1px solid ${DWA.railLine}`, padding: '0.5rem' }}>
          <Tooltip content={collapsed ? 'Expand navigation' : 'Collapse navigation'} disabled={!collapsed} delay={150}>
            <button
              type="button"
              onClick={onToggleCollapsed}
              aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              aria-expanded={!collapsed}
              aria-controls="hub-rail"
              className="ow-rail-row"
              data-testid="rail-collapse"
              style={{ ...rowStyle(false, collapsed), fontSize: '0.8rem', color: DWA.onDarkMuted }}
            >
              <RowIcon icon={collapsed ? faAnglesRight : faAnglesLeft} active={false} />
              {!collapsed && <span>Collapse</span>}
            </button>
          </Tooltip>
        </div>
      )}
      {openEntry && (
        <Flyout
          entry={openEntry}
          anchor={rowRefs.current[openEntry.key] ?? null}
          rail={railRef.current}
          panelRef={panelRef}
          onClose={close}
          onMouseEnter={cancel}
          onMouseLeave={hoverClose}
        />
      )}
    </nav>
  );
};
